import React, { useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import Select, { Field } from '../ReusableComponents/Select';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalWithResult } from '../../store/modalSlice';
import { createSchedule, fetchSchedules } from '../../store/agentScheduleSlice';
import { formatPersonaForPrompt, UNKNOWN_PERSONA_AUDIENCE } from '../../utils/persona';
import { usePostHogCapture } from '../../hooks/usePostHogCapture';
import { POSTHOG_EVENTS } from '../../utils/posthogEvents';
import { buildAssetContext } from '../../utils/assetContext';
import ModalBase from './ModalBase';
import ScheduleQuestionEditor, { ScheduleQuestionValue, isEmailConditionInvalid } from './schedule/ScheduleQuestionEditor';
import ScheduleFrequencyOptions from './schedule/ScheduleFrequencyOptions';
import {
  FrequencyType,
  buildRuntime,
  buildCronExpression,
  computeNextRunFromCron,
  isValidCron,
} from '../../utils/scheduleFrequency';
import type { AppDispatch, RootState } from '../../store';

const newQuestion = (): ScheduleQuestionValue => ({
  id: crypto.randomUUID(),
  title: '',
  prompt: '',
  attachments: [],
  mentionedAssets: [],
  sendEmail: false,
  emailMode: 'always',
  emailCondition: '',
});

interface CreateScheduleModalProps {
  onClose: (result?: any) => void;
  projectId?: string;
}

const ALL_TIMEZONES: string[] = (Intl as any).supportedValuesOf('timeZone');
const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const CreateScheduleModal: React.FC<CreateScheduleModalProps> = ({ onClose, projectId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { capture } = usePostHogCapture();

  // Pre-generate the schedule id so attachments can be uploaded to a stable, schedule-keyed GCS prefix before the schedule is persisted on save.
  const [scheduleId] = useState(() => crypto.randomUUID());

  const user = useSelector((state: any) => state.user);
  const preferredPersonaId = user?.permissionDetails?.preferredPersonaId ?? null;
  const personas = useSelector((state: any) => state.personas?.personas ?? {});
  // Assets-by-group dict so a mentioned group's member assets are listed in the baked context.
  const dataGroups = useSelector(
    (state: RootState) => state.assets.dataGroups as Record<string, { name: string }[]>
  );

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [freqType, setFreqType] = useState<FrequencyType>('Hourly');
  const [hour, setHour] = useState('9');
  const [minute, setMinute] = useState('00');
  const [ampm, setAmpm] = useState<'AM' | 'PM'>('AM');
  const [weekDay, setWeekDay] = useState('Mon');
  const [monthDay, setMonthDay] = useState(1);
  const [cronExpression, setCronExpression] = useState('');
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [questions, setQuestions] = useState<ScheduleQuestionValue[]>([newQuestion()]);
  const [isSaving, setIsSaving] = useState(false);
  // When a question opens the Draw on Map drawer, hide this modal so the map takes over the screen. The form stays mounted (display:none), so all entered state is preserved when the map closes.
  const [isMapOpen, setIsMapOpen] = useState(false);

  const handleClose = () => {
    dispatch(closeModalWithResult(null));
    onClose();
  };

  const handleSave = async () => {
    const validQuestions = questions.filter((q) => q.prompt.trim());
    if (!title.trim() || validQuestions.length === 0) return;
    if (freqType === 'Custom' && !isValidCron(cronExpression)) return;

    setIsSaving(true);
    try {
      const runtime = buildRuntime(freqType, hour, minute, ampm, weekDay, monthDay, cronExpression);
      const cron = buildCronExpression(freqType, hour, minute, ampm, weekDay, monthDay, cronExpression);
      const nextRunAt = computeNextRunFromCron(cron, timezone);

      // Mirror the chat path: role-free fallback when unset so run-time {{persona}} never renders blank.
      const persona =
        formatPersonaForPrompt(preferredPersonaId ? personas[preferredPersonaId] : null) ||
        UNKNOWN_PERSONA_AUDIENCE;

      await dispatch(createSchedule({
        id: scheduleId,
        title: title.trim(),
        description: description.trim() || undefined,
        runtime,
        cronExpression: cron,
        timezone,
        nextRunAt,
        ...(projectId && { projectId }),
        // No systemPrompt snapshot: the SystemPrompt model is no longer client-readable (issue #694), and the executor already resolves the DB-default template at run time (tier 2), so new schedules pick up prompt edits automatically. Existing schedules keep honoring their stored snapshot (tier 1).
        metadata: JSON.stringify({ persona }),
        runners: validQuestions.map((q) => ({
          id: q.id,
          title: q.title.trim() || q.prompt.trim().slice(0, 50),
          // Bake @-mention asset context into the stored prompt (no separate field needed).
          prompt: q.prompt.trim() + buildAssetContext(q.mentionedAssets ?? [], dataGroups),
          attachments: q.attachments?.length ? JSON.stringify(q.attachments) : undefined,
          sendEmail: q.sendEmail ?? false,
          // Condition only stored for gate/notify modes; dropped otherwise so a hidden stale condition is never stored on a runner that doesn't use it.
          emailCondition: q.sendEmail && (q.emailMode === 'gate' || q.emailMode === 'notify')
            ? q.emailCondition?.trim() || undefined
            : undefined,
          // null/undefined = "gate" downstream; only "notify" needs storing.
          emailConditionMode: q.sendEmail && q.emailMode === 'notify' ? 'notify' : undefined,
        })),
      })).unwrap();

      const conditionCount = validQuestions.filter((q) => q.sendEmail && (q.emailMode === 'gate' || q.emailMode === 'notify') && q.emailCondition?.trim()).length;
      if (conditionCount > 0) {
        capture(POSTHOG_EVENTS.SCHEDULE_EMAIL_CONDITION_SET, {
          questionCount: validQuestions.length,
          conditionCount,
        });
      }

      await dispatch(fetchSchedules());
      handleClose();
    } catch (err) {
      console.error('[CreateScheduleModal] Error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const updateQuestion = (index: number, patch: Partial<ScheduleQuestionValue>) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  // Blank-prompt questions are dropped at submit, so they must not hold Save hostage.
  const hasInvalidEmailCondition = questions.some((q) => q.prompt.trim() && isEmailConditionInvalid(q));
  const isCronInvalid = freqType === 'Custom' && !isValidCron(cronExpression);
  const isSaveDisabled =
    !title.trim() || questions.every((q) => !q.prompt.trim()) || isCronInvalid || hasInvalidEmailCondition || isSaving;

  return (
    <ModalBase
      title="New Agent"
      icon={<FiCalendar className="h-5 w-5" />}
      onClose={handleClose}
      // Full mobile height below md's full-screen breakpoint.
      className={`max-[741px]:h-[100dvh] max-[741px]:max-h-[100dvh]${isMapOpen ? ' !hidden' : ''}`}
      footer={
        <>
          <button onClick={handleClose} className="eai-btn-outline">
            Cancel
          </button>
          <button onClick={handleSave} disabled={isSaveDisabled} className="eai-btn-cta">
            {isSaving ? 'Creating…' : 'Create Agent'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-5">

        {/* Title */}
        <Field label="Title" htmlFor="schedule-title">
          <input
            id="schedule-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="eai-input"
            placeholder="e.g. Daily Market Summary"
          />
        </Field>

        {/* Description */}
        <Field label="Description (optional)" htmlFor="schedule-description">
          <input
            id="schedule-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="eai-input"
            placeholder="Brief description of this agent"
          />
        </Field>

        {/* Frequency + the field each cadence reveals */}
        <ScheduleFrequencyOptions
          freqType={freqType}
          onFreqTypeChange={setFreqType}
          hour={hour}
          onHourChange={setHour}
          minute={minute}
          onMinuteChange={setMinute}
          ampm={ampm}
          onAmpmChange={setAmpm}
          weekDay={weekDay}
          onWeekDayChange={setWeekDay}
          monthDay={monthDay}
          onMonthDayChange={setMonthDay}
          cronExpression={cronExpression}
          onCronExpressionChange={setCronExpression}
          isCronInvalid={isCronInvalid}
        />

        {/* Timezone */}
        <Select label="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
          {ALL_TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
          ))}
        </Select>

        {/* Questions (Runners) */}
        <div className="flex flex-col gap-3">
          <span className="eai-meta-label">Tasks</span>
          {questions.map((q, i) => (
            <ScheduleQuestionEditor
              key={q.id}
              value={q}
              scheduleId={scheduleId}
              index={i}
              onChange={(patch) => updateQuestion(i, patch)}
              onRemove={questions.length > 1 ? () => removeQuestion(i) : undefined}
              // Shared toggle is safe because only one map drawer can be open at a time (opening one hides the form); make this per-question if questions ever become independently interactive.
              onMapDrawerToggle={setIsMapOpen}
            />
          ))}
        </div>

      </div>
    </ModalBase>
  );
};

export default CreateScheduleModal;
